import {RadioGroup, FormControlLabel, Radio} from '@material-ui/core';
import React from 'react';
import {Platform, View, SafeAreaView, TouchableOpacity} from 'react-native';
import {WrappedFieldMetaProps} from 'redux-form';
import {images} from '../../../../../../assets/icons';
import {AddressSelect} from '../../../../../../shared/components/address-select';
import {useIsDesktop} from '../../../../../../shared/hooks/useIsDesktop';
import {DeviceSize} from '../../../../../../shared/theme/helpers';
import {DeliveryType} from '../../../../../../types/registerCardApplicationRequestPayload';
import {DesktopView} from './DesktopView';
import {useStyles} from '../../../../../../shared/hooks/styles';
import {useTheme} from '../../../../../../shared/theme';
import Screen from '../../../../../../shared/components/screen';
import {Text} from '../../../../../../shared/components/text';
import {Button} from '../../../../../../shared/components/button';
import {GetIssuePointCabinetResponsePayload} from '../../../../../../shared/models/api/getIssuePointCabinetResponsePayload';
import {GenericPointFeature, LngLat} from '@yandex/ymaps3-types';
import {MobileView} from './MobileView';

type Props = {
  selectedDeliveryType: DeliveryType;
  onDeliveryTypeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  mapData: GetIssuePointCabinetResponsePayload[];
  onIssuePointSelected: (issuePoint: GetIssuePointCabinetResponsePayload | null) => void;
  currentMarker: GetIssuePointCabinetResponsePayload | null;
  mapCenterPoint: [number, number];
  mapZoomLevel: number;
  isIssuePointDetailsVisible: boolean;
  setIsIssuePointDetailsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  markers: GenericPointFeature<LngLat>[];
  russianPostDeliveryAddress: string;
  setRussianPostDeliveryAddress: React.Dispatch<React.SetStateAction<string>>;
  isRussianPostDeliveryAddressValid: boolean;
  onClientRegisterOrderPress: () => Promise<void>;
  submitting: boolean;
  isDeliveryPointSelected: boolean;
  goBack: () => void;
  onGoBack: () => void;
};

export function EsiaRegCardMapWebLayout(props: Props) {
  const {
    selectedDeliveryType,
    onDeliveryTypeChange,
    mapData,
    onIssuePointSelected,
    currentMarker,
    mapCenterPoint,
    mapZoomLevel,
    isIssuePointDetailsVisible,
    setIsIssuePointDetailsVisible,
    markers,
    russianPostDeliveryAddress,
    setRussianPostDeliveryAddress,
    isRussianPostDeliveryAddressValid,
    onClientRegisterOrderPress,
    submitting,
    isDeliveryPointSelected,
    goBack,
    onGoBack,
  } = props;
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const styles = useStyles(({mediaOnly}) => ({
    root: {
      margin: Platform.OS === 'ios' ? 1 : 0,
      position: 'relative',
      paddingBottom: 0,
      overflow: 'hidden',
      alignItems: 'center',
      ...mediaOnly(DeviceSize.tablet, {width: '100%'}),
      ...mediaOnly(DeviceSize.mobile, {width: '100%'}),
    },
    radioGroupContainer: {
      paddingVertical: 6,
    },
    btnContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 12,
      width: '100%',
      marginVertical: 16,
    },
    btn: {
      width: isDesktop ? '38%' : 260,
      textAlign: 'center',
      padding: 6,
      zIndex: 3,
    },
    header: {
      ...theme.topBar.header,
      elevation: 6,
      minHeight: 54,
      paddingRight: 10,
      flexDirection: 'row',
      alignItems: 'center',
    },
    headerBackBtn: {
      width: 48,
      height: 48,
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerTitle: {
      paddingRight: 24,
    },
    safearea: {
      backgroundColor: theme.color.backgroundMenuColor,
    },
  }));

  const MainContent = (
    <View style={styles.root}>
      <View style={styles.radioGroupContainer}>
        <RadioGroup
          row
          aria-label='delivery-type'
          name='DeliveryType'
          value={selectedDeliveryType}
          onChange={onDeliveryTypeChange}>
          <FormControlLabel
            value={DeliveryType.IssuePoint}
            control={<Radio style={{color: theme.color.primaryColor}} />}
            label='Пункт выдачи'
          />
          <FormControlLabel
            value={DeliveryType.RussianPost}
            control={<Radio style={{color: theme.color.primaryColor}} />}
            label='Почта России'
          />
        </RadioGroup>
      </View>
      {selectedDeliveryType !== DeliveryType.RussianPost ? (
        isDesktop ? (
          <DesktopView
            mapData={mapData}
            onPressMarker={onIssuePointSelected}
            currentMarker={currentMarker}
            centerPoint={mapCenterPoint}
            zoomLevel={mapZoomLevel}
            isIssuePointDetailsVisible={isIssuePointDetailsVisible}
            setIsIssuePointDetailsVisible={setIsIssuePointDetailsVisible}
            markers={markers}
          />
        ) : (
          <MobileView
            centerPoint={mapCenterPoint}
            currentMarker={currentMarker}
            mapData={mapData}
            onPressMarker={onIssuePointSelected}
            zoomLevel={mapZoomLevel}
            isIssuePointDetailsVisible={isIssuePointDetailsVisible}
            setIsIssuePointDetailsVisible={setIsIssuePointDetailsVisible}
          />
        )
      ) : (
        <View style={{width: '80%', marginBottom: 10}}>
          <AddressSelect
            name='targetAdress'
            label='Адрес получателя'
            value={russianPostDeliveryAddress}
            baseFieldProps={{
              style: {width: '100%'},
              autoFocus: true,
              meta: {
                error: {error: 'Адрес должен относиться к Свердловской области'},
                touched: !isRussianPostDeliveryAddressValid,
              } as WrappedFieldMetaProps,
            }}
            onChange={setRussianPostDeliveryAddress}
            setAddress={setRussianPostDeliveryAddress}
          />
        </View>
      )}

      <View style={styles.btnContainer}>
        {isDesktop && (
          <Button id='go_back' type='secondary' text='Вернуться назад' style={styles.btn} onPress={onGoBack} />
        )}
        <Button
          id='esia_submit_order'
          type='primary'
          text='Отправить заявление'
          style={styles.btn}
          onPress={onClientRegisterOrderPress}
          disabled={
            !isDeliveryPointSelected ||
            submitting ||
            (selectedDeliveryType === DeliveryType.RussianPost && !isRussianPostDeliveryAddressValid)
          }
        />
      </View>
    </View>
  );

  return isDesktop ? (
    MainContent
  ) : (
    <Screen>
      <SafeAreaView style={styles.safearea}>
        <View testID='header-container' style={styles.header}>
          <TouchableOpacity onPress={goBack} style={styles.headerBackBtn} testID='header-content'>
            <images.Left height={24} width={24} color={theme.color.primaryColor} />
          </TouchableOpacity>
          <Text type='h6' style={styles.headerTitle}>
            Выбор способа получения
          </Text>
        </View>
        {MainContent}
      </SafeAreaView>
    </Screen>
  );
}
